import { useEffect } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { FixMe } from '@utils/types';
import useStores from '@stores/useStores';
import { ApiGetResource, fetchData, swrConfigDefaults } from '@api';

import { AccountManagementStore } from '../store';

export const getResourceCacheKey = (
  workspaceId: string,
  resourceDefinitionName: string,
  apiResourceName: ApiGetResource,
) => `${workspaceId}-${resourceDefinitionName}-${apiResourceName}`;

export const useLoadAccountsIntoAccountManagementStore = ({
  accountManagementStore,
  legacyAccountsRole,
}: {
  accountManagementStore: AccountManagementStore;
  legacyAccountsRole?: string;
}) => {
  const { workspaceStore, messagesStore } = useStores();
  const {
    setAccountDefinitions,
    setAccounts,
    setLegacyAccounts,
    supportedAccountTypes,
    resourceDefinitionName,
  } = accountManagementStore;

  // Fetch account definitions
  const { isLoading: loadingAccountDefinitions, data: accountDefApiResponse } = useSWRImmutable(
    getResourceCacheKey(
      workspaceStore.id,
      resourceDefinitionName,
      ApiGetResource.ACCOUNT_DEFINITIONS,
    ),
    fetchData({
      path: 'web',
      resource: ApiGetResource.ACCOUNT_DEFINITIONS,
      errorMessage: 'Failed to fetch account definitions',
      onError: (e) => messagesStore.showErrorMessage(e),
    }),
    swrConfigDefaults,
  );

  // Fetch accounts(migrated) only if the destination supports accounts types

  const { isLoading: loadingAccounts, accountsApiResponse } = (() => {
    if (supportedAccountTypes.length > 0) {
      const { isLoading, data: accountsApiResponse } = useSWR(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
        fetchData({
          path: `/web/workspaces/${workspaceStore.id}`,
          resource: ApiGetResource.ACCOUNTS,
          errorMessage: 'Failed to fetch destination accounts',
          params: {
            definitionNames: supportedAccountTypes.join(','),
          },
          onError: (e) => messagesStore.showErrorMessage(e),
        }),
        swrConfigDefaults,
      );

      return { isLoading, accountsApiResponse };
    }

    return { isLoading: false, accountsApiResponse: undefined };
  })();

  // Fetch legacy accounts
  const path = `/workspaces/${workspaceStore.id}/destination/accounts/${legacyAccountsRole}`;

  const { isLoading: loadingLegacyAccounts, data: legacyAccountsApiResponse } = useSWR(
    legacyAccountsRole
      ? getResourceCacheKey(
          workspaceStore.id,
          resourceDefinitionName,
          ApiGetResource.ACCOUNTS_LEGACY,
        )
      : null,
    fetchData<ApiGetResource.ACCOUNTS_LEGACY>({
      path,
      errorMessage: 'Failed to fetch destination accounts',
      onError: (e) => messagesStore.showErrorMessage(e),
    }),
    swrConfigDefaults,
  );

  useEffect(() => {
    if (accountsApiResponse) {
      setAccounts(accountsApiResponse.data);
    }
    if (accountDefApiResponse) {
      setAccountDefinitions(accountDefApiResponse.data);
    }
    if (legacyAccountsApiResponse) {
      setLegacyAccounts(legacyAccountsApiResponse as FixMe);
    }
  }, [accountsApiResponse, accountDefApiResponse, legacyAccountsApiResponse]);

  return { isLoading: loadingAccountDefinitions || loadingAccounts || loadingLegacyAccounts };
};
